/*===================================
||
|| PhotoModBatchesContext
||
===================================*/
import React, {
    createContext,
    useMemo,
    useEffect,
    useContext,
    useRef
} from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

// redux
import {
    batchesSlice,
    BatchThunks,
    defaultFilters
} from "frontend/components/fort/mod/photos/slices/batchesSlice";

// components
export { LoadMoreButton } from "./components/LoadMoreButton.js";
export { FilterByBatchType } from "./components/FilterByBatchType.js";
export { Search } from "./components/Search.js";
export { SortBy } from "./components/SortBy.js";
export { StatusMessage } from "./components/StatusMessage.js";

// hooks
import { useUrlSearchParams } from "frontend/hooks/useUrlSearchParams";
import { useAuth } from "frontend/hooks/useAuth";

// data
import {
    BATCH_TYPE_KEYS,
    SORT_BY_VALUES,
    QUEUES
} from "frontend/components/fort/mod/photos/constants.js";

/*---------------------------
| Context
---------------------------*/
const PhotoModBatchesContext = createContext();

// Provider
export const PhotoModBatchesContextProvider = ({
    children,
    moderationType = "ANY",
    currentQueue = QUEUES.VIS_MOD,
    showOnlySuspendedCreators = false
}) => {
    const refinedProps = {
        moderationType,
        showOnlySuspendedCreators,
        currentQueue
    };

    // useMemo so it does not pass value on every render
    const value = useMemo(() => refinedProps, [refinedProps]);

    return (
        <PhotoModBatchesContext.Provider value={value}>
            <MountingWrapper>{children}</MountingWrapper>
        </PhotoModBatchesContext.Provider>
    );
};

// Display Name
PhotoModBatchesContext.displayName = "PhotoModBatches";

// prop-types
PhotoModBatchesContextProvider.propTypes = {
    children: PropTypes.any,
    moderationType: PropTypes.string,
    currentQueue: PropTypes.string,
    showOnlySuspendedCreators: PropTypes.bool
};

// usePhotoModBatches
export const usePhotoModBatches = () => {
    const {
        moderationType,
        showOnlySuspendedCreators,
        currentQueue
    } = useContext(PhotoModBatchesContext);
    const dispatch = useDispatch();
    const batchesState = useSelector(state => state.batches);
    const { urlSearchParams, setUrlSearchParam } = useUrlSearchParams();
    const { currentUser } = useAuth();

    // User should not be able to mess with UI while calling api to avoid race conditions
    const isCallingApi = batchesState.apiStatus === "calling-api";

    // Magical Api Async Method
    const apiCall = async pages => {
        dispatch(batchesSlice.actions.setIsProcessing(true));
        dispatch(
            BatchThunks.fetchBatches({
                moderationType,
                showOnlySuspendedCreators,
                currentQueue,
                pages,
                setUrlSearchParam,
                currentUser
            })
        );
    };

    const fetchCurrentPage = () => {
        // Prioritize urlSearchParam
        let urlSearchParamCurrentPage = Number(urlSearchParams.currentPage);

        // must be missing from urlSearchParams, adding and setting a default, must be a number
        if (isNaN(urlSearchParamCurrentPage)) {
            urlSearchParamCurrentPage = 1;
            setUrlSearchParam({
                key: "currentPage",
                value: urlSearchParamCurrentPage
            });
        }

        // fetch Batches
        apiCall(urlSearchParamCurrentPage);
    };

    const setFilter = filter => {
        if (!isCallingApi) {
            dispatch(batchesSlice.actions.setFilter(filter));
        }
    };
    const setBatchTypeFilter = batchTypeFilter => {
        if (!isCallingApi) {
            dispatch(batchesSlice.actions.setBatchTypeFilter(batchTypeFilter));
        }
    };

    return {
        ...batchesState,
        isCallingApi,
        dispatch,
        apiCall,
        fetchCurrentPage,
        setFilter,
        setBatchTypeFilter
    };
};

// MountingWrapper
const MountingWrapper = ({ children }) => {
    // keeps track of any synchronization we need to do between RTK State and Other State Evaluations
    // Currently only used by DOM urlSearchParams, but could also be used for other things
    const { urlSearchParams, setUrlSearchParam } = useUrlSearchParams();

    const previousTextSearch = useRef("");

    const {
        dispatch,
        apiCall,
        fetchCurrentPage,
        hasLoaded,

        // UI changes
        filters: { sortBy, textSearch, batchType },
        pagination: { currentPage }
    } = usePhotoModBatches();

    /*---------------------------
    | methods
    ---------------------------*/
    const updateUrlSearchParamOrState = ({
        key,
        reduxAction,
        defaultValue,
        isBoolean = false,
        validate = () => true
    }) => {
        // prioritize urlSearchParams over default state
        if (urlSearchParams[key] && validate(urlSearchParams[key])) {
            // convert urlSearchParams string true/false to boolean
            const value = isBoolean
                ? urlSearchParams[key] === "true"
                : urlSearchParams[key];
            dispatch(reduxAction({ [key]: value }));
        } else {
            setUrlSearchParam({ key, value: defaultValue });
        }
    };

    const resolveOnload = () => {
        updateUrlSearchParamOrState({
            key: "sortBy",
            reduxAction: batchesSlice.actions.setFilter,
            defaultValue: defaultFilters.sortBy,
            validate: urlSearchParam => SORT_BY_VALUES.includes(urlSearchParam)
        });
        updateUrlSearchParamOrState({
            key: "textSearch",
            reduxAction: batchesSlice.actions.setFilter,
            defaultValue: defaultFilters.textSearch
        });

        // batchType
        Object.keys(BATCH_TYPE_KEYS).forEach(objProp => {
            const batchTypeKey = BATCH_TYPE_KEYS[objProp];

            updateUrlSearchParamOrState({
                key: batchTypeKey,
                reduxAction: batchesSlice.actions.setBatchTypeFilter,
                defaultValue: "false",
                isBoolean: true
            });
        });

        fetchCurrentPage();
    };

    // throttling api requests in case user is making multiple filter changes all at once.
    let timeoutId;
    const debounceFilterChangeApiCall = () => {
        dispatch(batchesSlice.actions.setShowLoading(true));
        clearTimeout(timeoutId); // every time this is called, we clear previous requests
        timeoutId = setTimeout(() => {
            // this initiates the api call
            apiCall(1);
        }, 2000);
    };

    /*---------------------------
    | Side Effects
    ---------------------------*/
    // On Mount
    useEffect(() => {
        resolveOnload();
    }, []);

    // These Filter changes initiate the resetting of batch data and another api call.
    useEffect(() => {
        if (hasLoaded) {
            debounceFilterChangeApiCall();
        }
    }, [sortBy, textSearch, batchType]);

    // When any of these props change, we update the urlParams
    useEffect(() => {
        if (hasLoaded) {
            if (currentPage) {
                setUrlSearchParam({ key: "currentPage", value: currentPage });
            }
            if (batchType) {
                Object.keys(batchType).forEach(key => {
                    const value = batchType?.[key] === true; // if null or false, should always be false
                    setUrlSearchParam({ key, value });
                });
            }
            if (sortBy) {
                setUrlSearchParam({ key: "sortBy", value: sortBy });
            }

            // Compare the current textSearch with the previousTextSearch, accounting for empty strings as well.
            if (textSearch !== previousTextSearch.current) {
                setUrlSearchParam({ key: "textSearch", value: textSearch });
            }

            // Update previousTextSearch with the current textSearch
            previousTextSearch.current = textSearch;
        }
    }, [currentPage, sortBy, textSearch, batchType, hasLoaded]);

    return <>{children}</>;
};

// prop-types
MountingWrapper.propTypes = {
    children: PropTypes.any
};
